import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../auth/AuthContext";
import { Logo } from "../components/Logo";
import {
  LogOut,
  ArrowLeft,
  HandHeart,
  Leaf,
  Loader2,
  Users,
} from "lucide-react";

const wards = [
  "Ward 1 · Shanala",
  "Ward 2 · Sanala Road",
  "Ward 3 · Ravapar",
  "Ward 4 · Mahendra Nagar",
  "Ward 5 · Sardar Nagar",
  "Ward 6 · Lati Plot",
  "Ward 7 · Sanalapar",
  "Ward 8 · Trajpar",
];

const Dashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [pledges, setPledges] = useState([]);
  const [loadingPledges, setLoadingPledges] = useState(true);
  const [totalPledges, setTotalPledges] = useState(0);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    ward: wards[0],
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
    if (user) {
      setForm((f) => ({ ...f, name: user.name || "" }));
    }
  }, [loading, user, navigate]);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/pledges`, { withCredentials: true }),
        axios.get(`${API}/pledges/count`),
      ]);
      setPledges(p.data);
      setTotalPledges(c.data.total);
    } catch {}
    setLoadingPledges(false);
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/pledges`, form, { withCredentials: true });
      setMsg("Pledge recorded. See you Sunday.");
      setForm({ ...form, phone: "" });
      load();
      setTimeout(() => setMsg(null), 2200);
    } catch {
      setMsg("Could not submit — please try again.");
      setTimeout(() => setMsg(null), 2200);
    }
    setSubmitting(false);
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--mati-sand)]">
        <Loader2 className="w-6 h-6 animate-spin text-[color:var(--mati-clay)]" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[color:var(--mati-sand)] grain relative"
      data-testid="dashboard-page"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[color:var(--mati-sand)]/80 border-b border-[color:var(--mati-border)]">
        <div className="max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="dashboard-home-link"
            className="flex items-center gap-2.5"
          >
            <Logo className="h-7 w-7" />
            <div className="leading-none">
              <div className="font-display text-[14px] font-extrabold tracking-[0.14em]">
                MĀTI
              </div>
              <div className="font-mono-ui text-[9px] tracking-[0.32em] text-[color:var(--mati-muted)] mt-0.5">
                CITIZEN · DASHBOARD
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 border border-[color:var(--mati-border)] px-4 py-2 rounded-full font-sub text-[12px] tracking-widest uppercase font-semibold hover:bg-white"
              data-testid="dashboard-back-link"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back home
            </Link>
            <div className="flex items-center gap-2">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover border border-[color:var(--mati-border)]"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] flex items-center justify-center text-xs font-sub font-semibold">
                  {user.name?.[0]?.toUpperCase() || "M"}
                </div>
              )}
              <div className="hidden sm:block leading-tight">
                <div className="font-sub text-[13px] font-semibold">
                  {user.name}
                </div>
                <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[color:var(--mati-muted)]">
                  {user.email}
                </div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="dashboard-logout-btn"
              className="inline-flex items-center gap-2 bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] px-4 py-2 rounded-full font-sub text-[12px] tracking-widest uppercase font-semibold hover:bg-[color:var(--mati-clay)] transition-all"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12 py-14">
        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-10"
        >
          <div className="eyebrow text-[color:var(--mati-clay)]">
            Namaste, Morbikar
          </div>
          <h1 className="font-display font-extrabold text-5xl sm:text-6xl text-[color:var(--mati-forest)] mt-4 leading-[0.95] tracking-tight">
            {user.name?.split(" ")[0] || "Citizen"},{" "}
            <span className="italic text-[color:var(--mati-clay)]">
              welcome back.
            </span>
          </h1>
          <p className="mt-4 font-sub text-base text-[color:var(--mati-muted)] max-w-xl">
            Pledge the next drive, see your history, and track our growing
            civic movement — all from one place.
          </p>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          {/* Pledge form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 lg:col-span-7 bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] rounded-3xl p-8 sm:p-10 relative overflow-hidden"
            data-testid="pledge-form"
          >
            <div className="absolute inset-0 ceramic-grid-dark opacity-30 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--mati-clay)] flex items-center justify-center">
                  <HandHeart className="w-5 h-5" />
                </div>
                <div>
                  <div className="eyebrow text-[color:var(--mati-clay)]">
                    New Pledge
                  </div>
                  <div className="font-display text-2xl font-bold mt-0.5">
                    Join the next drive
                  </div>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-1">
                  <label className="eyebrow text-[color:var(--mati-sand)]/70">
                    Name
                  </label>
                  <input
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                    data-testid="pledge-name-input"
                    className="mt-2 w-full bg-[color:var(--mati-forest-2)] border border-[color:var(--mati-border-dark)] rounded-xl px-4 py-3 font-sub text-[15px] focus:border-[color:var(--mati-clay)] outline-none text-[color:var(--mati-sand)]"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="eyebrow text-[color:var(--mati-sand)]/70">
                    Phone
                  </label>
                  <input
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                    data-testid="pledge-phone-input"
                    className="mt-2 w-full bg-[color:var(--mati-forest-2)] border border-[color:var(--mati-border-dark)] rounded-xl px-4 py-3 font-sub text-[15px] focus:border-[color:var(--mati-clay)] outline-none text-[color:var(--mati-sand)]"
                    placeholder="+91 98XXXXXXXX"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="eyebrow text-[color:var(--mati-sand)]/70">
                    Ward
                  </label>
                  <select
                    value={form.ward}
                    onChange={(e) =>
                      setForm({ ...form, ward: e.target.value })
                    }
                    data-testid="pledge-ward-select"
                    className="mt-2 w-full bg-[color:var(--mati-forest-2)] border border-[color:var(--mati-border-dark)] rounded-xl px-4 py-3 font-sub text-[15px] focus:border-[color:var(--mati-clay)] outline-none text-[color:var(--mati-sand)]"
                  >
                    {wards.map((w) => (
                      <option key={w}>{w}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="pledge-submit-btn"
                className="mt-6 w-full bg-[color:var(--mati-clay)] text-[color:var(--mati-sand)] px-6 py-4 rounded-full font-sub text-[13px] tracking-widest uppercase font-semibold hover:bg-[color:var(--mati-sand)] hover:text-[color:var(--mati-forest)] transition-all disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Record my pledge"}
              </button>
              {msg && (
                <div
                  className="mt-4 font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[color:var(--mati-clay)]"
                  data-testid="pledge-msg"
                >
                  {msg}
                </div>
              )}
            </div>
          </motion.form>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="col-span-12 lg:col-span-5 flex flex-col gap-6"
          >
            <div
              className="bg-white border border-[color:var(--mati-border)] rounded-3xl p-8"
              data-testid="total-pledges-card"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--mati-sand-2)] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[color:var(--mati-forest)]" />
                </div>
                <div className="eyebrow text-[color:var(--mati-forest)]">
                  Total Pledges
                </div>
              </div>
              <div className="font-display font-extrabold text-6xl sm:text-7xl text-[color:var(--mati-forest)] mt-6 leading-none tracking-tight">
                {totalPledges}
              </div>
              <div className="mt-3 font-sub text-sm text-[color:var(--mati-muted)]">
                Morbikars pledged to join the movement.
              </div>
            </div>

            <div
              className="bg-[color:var(--mati-sand-2)] border border-[color:var(--mati-border)] rounded-3xl p-8"
              data-testid="my-pledges-card"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--mati-clay)] flex items-center justify-center">
                  <Leaf className="w-5 h-5 text-[color:var(--mati-sand)]" />
                </div>
                <div className="eyebrow text-[color:var(--mati-clay)]">
                  My Pledges
                </div>
              </div>
              <div className="font-display font-extrabold text-6xl sm:text-7xl text-[color:var(--mati-forest)] mt-6 leading-none tracking-tight">
                {pledges.length}
              </div>
              <div className="mt-3 font-sub text-sm text-[color:var(--mati-muted)]">
                Your personal civic contribution so far.
              </div>
            </div>
          </motion.div>

          {/* History */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="col-span-12 bg-white border border-[color:var(--mati-border)] rounded-3xl p-8 sm:p-10"
            data-testid="pledge-history-card"
          >
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="eyebrow text-[color:var(--mati-clay)]">
                  History
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-[color:var(--mati-forest)] mt-2">
                  Your pledges
                </h2>
              </div>
              <div className="font-mono-ui text-[10px] uppercase tracking-[0.28em] text-[color:var(--mati-muted)] hidden sm:block">
                Sorted · newest first
              </div>
            </div>

            {loadingPledges ? (
              <div className="font-mono-ui text-[11px] uppercase tracking-[0.3em] text-[color:var(--mati-muted)]">
                Loading…
              </div>
            ) : pledges.length === 0 ? (
              <div className="font-sub text-sm text-[color:var(--mati-muted)]">
                No pledges yet — your first one is one tap away.
              </div>
            ) : (
              <ul className="divide-y divide-[color:var(--mati-border)]">
                {pledges.map((p) => (
                  <li
                    key={p.pledge_id}
                    data-testid={`pledge-row-${p.pledge_id}`}
                    className="py-5 flex items-center justify-between gap-6"
                  >
                    <div>
                      <div className="font-display text-lg font-semibold text-[color:var(--mati-forest)]">
                        {p.ward}
                      </div>
                      <div className="font-sub text-[13px] text-[color:var(--mati-muted)] mt-1">
                        {p.name} · {p.phone}
                      </div>
                    </div>
                    <div className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-[color:var(--mati-muted)] shrink-0">
                      {new Date(p.created_at).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
